/** The board's row/lane layout, extracted pure so BOTH the view and the host
 *  compute the identical packing — the host needs it to append a tapped code
 *  after the last bar of the selected lane. Bars PINNED to a lane (manual
 *  vertical drop) claim their spot first; the rest greedy-pack around them. */
import type { ScopeMember } from "../../../models";

export interface BarSpan {
  startIndex: number;
  durationDays: number;
}

export interface Bar {
  member: ScopeMember;
  startIndex: number;
  durationDays: number;
  lane: number;
}

export interface LeafRow {
  key: string;
  label: string;
  path: string[];
  bars: Bar[];
  undated: ScopeMember[];
  lanes: number;
}

/** A member's display start/finish: its planned dates, or its actuals for
 *  completed work (restart/reend are NULLed by mark_complete). */
export const memberStart = (m: ScopeMember): string | null =>
  m.restart_date ?? m.actual_start;
export const memberFinish = (m: ScopeMember): string | null =>
  m.reend_date ?? m.actual_finish;

export function spanOf(
  m: ScopeMember,
  calendarIndexFor: (iso: string | null) => number,
): BarSpan {
  const start = memberStart(m);
  const finish = memberFinish(m);
  const startIndex = calendarIndexFor(start);
  let durationDays = 1;
  if (start && finish) {
    const endIdx = calendarIndexFor(finish);
    if (endIdx >= startIndex) durationDays = endIdx - startIndex + 1;
  } else if (m.original_duration != null) {
    durationDays = Math.max(1, Math.round(m.original_duration));
  }
  return { startIndex, durationDays };
}

export interface LeafRowOpts {
  members: ScopeMember[];
  calendarIndexFor: (iso: string | null) => number;
  spanOverrides?: Map<number, BarSpan>;
  laneOverrides?: Map<number, number>;
  rowOrder?: string[];
  extraRows?: Array<{ key: string; label: string }>;
  extraLanes?: Map<string, number>;
  seedLeaves?: Array<{ key: string; label: string; path: string[] }>;
  /** Each leaf's Takt identity — when present, leaves default-order by Takt
   *  (section → zone → level) instead of alphabetical WBS name. Manual rowOrder
   *  still wins. */
  taktByLeaf?: Map<
    string,
    { takt_a: string | null; takt_b: string | null; takt_c: string | null }
  >;
  /** Lane grouping by FS chain: activity_id → the in-leaf activity_id it follows
   *  Finish→Start. A bar joins that predecessor's lane (continuing the chain);
   *  any bar without an FS link here (no tie, or an SS/FF/SF tie) starts a NEW
   *  lane. When absent, lanes fall back to greedy time-packing. */
  fsLinks?: Map<number, number>;
  /** Render completed (TK_Complete) work as bars too — for the read-only
   *  completed-plan view, where the whole point is to see what the plan did.
   *  The live board omits completed work (default false). */
  showCompleted?: boolean;
}

/** Pull the trailing number out of a Takt_C ("LVL3" → 3); large fallback so
 *  un-levelled leaves sort last within their zone. */
function levelNum(takt_c: string | null | undefined): number {
  const m = (takt_c || "").match(/(\d+)/);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

export function computeLeafRows({
  members,
  calendarIndexFor,
  spanOverrides,
  laneOverrides,
  rowOrder,
  extraRows,
  extraLanes,
  seedLeaves,
  taktByLeaf,
  fsLinks,
  showCompleted,
}: LeafRowOpts): LeafRow[] {
  const byLeaf = new Map<string, LeafRow>();
  // Every scope leaf gets a row even when nothing on it renders a bar (all
  // deleted or all completed) — a wiped board still shows its structure.
  for (const s of seedLeaves ?? []) {
    if (!byLeaf.has(s.key)) {
      byLeaf.set(s.key, {
        key: s.key,
        label: s.label,
        path: s.path,
        bars: [],
        undated: [],
        lanes: 1,
      });
    }
  }
  for (const m of members) {
    const path = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
    const key = path.join(" ");
    let row = byLeaf.get(key);
    if (!row) {
      row = {
        key,
        label: path[path.length - 1],
        path,
        bars: [],
        undated: [],
        lanes: 1,
      };
      byLeaf.set(key, row);
    }
    // Completed work is locked history — omitted on the live board, but shown
    // (positioned from its actual dates) on the read-only completed-plan view.
    if (!showCompleted && m.status === "TK_Complete") continue;
    const span = spanOverrides?.get(m.activity_id) ?? spanOf(m, calendarIndexFor);
    if (span.startIndex >= 0) {
      row.bars.push({ member: m, ...span, lane: 0 });
    } else {
      row.undated.push(m);
    }
  }
  const alpha = (a: LeafRow, b: LeafRow) =>
    a.path.join(" ").localeCompare(b.path.join(" "), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  const rows = Array.from(byLeaf.values()).sort((a, b) => {
    if (taktByLeaf) {
      const ta = taktByLeaf.get(a.key);
      const tb = taktByLeaf.get(b.key);
      const sa = (ta?.takt_a || "~").localeCompare(tb?.takt_a || "~", undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (sa !== 0) return sa;
      const sb = (ta?.takt_b || "~").localeCompare(tb?.takt_b || "~", undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (sb !== 0) return sb;
      const sc = levelNum(ta?.takt_c) - levelNum(tb?.takt_c);
      if (sc !== 0) return sc;
    }
    return alpha(a, b);
  });
  for (const ex of extraRows ?? []) {
    if (!byLeaf.has(ex.key)) {
      rows.push({
        key: ex.key,
        label: ex.label,
        path: [ex.label],
        bars: [],
        undated: [],
        lanes: 1,
      });
    }
  }
  if (rowOrder && rowOrder.length > 0) {
    const pos = new Map(rowOrder.map((k, i) => [k, i]));
    rows.sort((a, b) => {
      const pa = pos.get(a.key);
      const pb = pos.get(b.key);
      if (pa != null && pb != null) return pa - pb;
      if (pa != null) return -1;
      if (pb != null) return 1;
      return 0;
    });
  }
  for (const row of rows) {
    row.bars.sort(
      (a, b) => a.startIndex - b.startIndex || a.durationDays - b.durationDays,
    );
    const laneIntervals: Array<Array<{ s: number; e: number }>> = [];
    const put = (lane: number, s: number, e: number) => {
      while (laneIntervals.length <= lane) laneIntervals.push([]);
      laneIntervals[lane].push({ s, e });
    };
    const fits = (lane: number, s: number, e: number) =>
      (laneIntervals[lane] ?? []).every((iv) => e <= iv.s || s >= iv.e);
    // A pinned bar (a manual drop) claims its lane — but bars NEVER cover one
    // another: if that lane is already taken at this time, the bar slides DOWN to
    // the first free lane below its pin (a new lane if need be). So an untouched
    // bar keeps its lane and the one dropped onto an occupied spot pushes down to
    // make room. Auto rules (FS-chain + greedy) only place bars with NO pin.
    // laneOfActivity: where each activity actually landed (FS successors follow
    // their predecessor's lane).
    const laneOfActivity = new Map<number, number>();
    const greedy: Bar[] = [];
    for (const bar of row.bars) {
      const pin = laneOverrides?.get(bar.member.activity_id);
      const s = bar.startIndex;
      const e = bar.startIndex + bar.durationDays;
      if (pin != null && pin >= 0) {
        let lane = pin;
        while (!fits(lane, s, e)) lane++;
        bar.lane = lane;
        put(lane, s, e);
        laneOfActivity.set(bar.member.activity_id, lane);
      } else {
        greedy.push(bar);
      }
    }
    for (const bar of greedy) {
      const s = bar.startIndex;
      const e = bar.startIndex + bar.durationDays;
      let placed = -1;
      // FS chain FIRST: keep a bar on the lane of the activity it follows F→S, so
      // a run reads as one row. (An earlier FS sibling may already hold the lane —
      // then this branch falls through to greedy and lands elsewhere.)
      if (fsLinks) {
        const predId = fsLinks.get(bar.member.activity_id);
        if (predId != null) {
          const predLane = laneOfActivity.get(predId);
          if (predLane != null && fits(predLane, s, e)) placed = predLane;
        }
      }
      // Otherwise greedy first-fit, so SS/FF/untied work fills gaps instead of
      // leaving empty lanes — a mix of chain + greedy packing.
      if (placed === -1) {
        for (let i = 0; i < laneIntervals.length; i++) {
          if (fits(i, s, e)) {
            placed = i;
            break;
          }
        }
        if (placed === -1) placed = laneIntervals.length;
      }
      put(placed, s, e);
      bar.lane = placed;
      laneOfActivity.set(bar.member.activity_id, placed);
    }
    row.lanes = Math.max(1, laneIntervals.length) + (extraLanes?.get(row.key) ?? 0);
  }
  return rows;
}

/** The lane index a pointer at `clientY` sits over, measured against the TOP of a
 *  row's LANES area (`.wbs-board-cells`) — NOT the row wrapper, which includes the
 *  zone header band and would offset every lane by the header height (a pure
 *  horizontal drag would then read as a one-lane drop). Single-bar and bulk drags
 *  both go through this so they can never diverge. The `- 4` is the lanes area's
 *  top padding. */
export function laneAtY(clientY: number, cellsTop: number, laneHeight: number): number {
  return Math.max(0, Math.floor((clientY - cellsTop - 4) / laneHeight));
}

/** Per-bar target lanes for a bulk drag. Each selected bar shifts by the same
 *  number of lanes the grabbed bar moved (targetLane − its home lane), clamped at
 *  0 — so the selection keeps its shape and lands where you dropped it, the same
 *  free placement a single-bar drag gets. A purely horizontal move (delta 0) pins
 *  every bar to its CURRENT lane, freezing it so a left/right drag never re-packs
 *  it onto a new lane. */
export function bulkLaneShift(
  ids: number[],
  homeLaneById: Map<number, number>,
  grabbedHomeLane: number,
  targetLane: number,
): Array<[number, number]> {
  const delta = targetLane - grabbedHomeLane;
  return ids.map((id) => [id, Math.max(0, (homeLaneById.get(id) ?? 0) + delta)]);
}
